/**
 * Rendu Markdown minimal, sans dépendance, pour afficher les documents de
 * `docs/` dans le jeu (panneau Dev). Couvre ce que ces fichiers utilisent :
 * titres, paragraphes, listes, tableaux, citations, blocs et code en ligne,
 * gras, italique, liens, règles horizontales. Tout le HTML source est échappé.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Marqueur interne pour protéger le code en ligne du gras/italique. */
const CODE_MARK = ' ';

/**
 * Balises HTML tolérées dans les documents (style de la spec : <ins>, <font color>).
 * Tout le reste reste échappé — liste blanche stricte, sans attributs libres.
 */
function restoreAllowedTags(html: string): string {
  return html
    .replace(/&lt;(\/?)(ins|u|sub|sup|br)&gt;/g, '<$1$2>')
    .replace(/&lt;font color=(?:'|&quot;)([a-zA-Z]+)(?:'|&quot;)&gt;/g, '<span style="color:$1">')
    .replace(/&lt;\/font&gt;/g, '</span>');
}

/** Mise en forme en ligne (après échappement). */
function inline(text: string): string {
  let out = restoreAllowedTags(escapeHtml(text));
  const codes: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_m, c: string) => {
    codes.push(`<code>${c}</code>`);
    return `${CODE_MARK}${codes.length - 1}${CODE_MARK}`;
  });
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*\w])\*([^*\n]+)\*(?!\w)/g, '$1<em>$2</em>');
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  // Liens relatifs vers d'autres docs : simple texte souligné (pas de navigation).
  out = out.replace(/\[([^\]]+)\]\([^)\s]+\)/g, '<u>$1</u>');
  out = out.replace(new RegExp(`${CODE_MARK}(\\d+)${CODE_MARK}`, 'g'), (_m, i: string) => codes[Number(i)] ?? '');
  return out;
}

function renderTable(rows: string[]): string {
  const cells = (line: string): string[] =>
    line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((c) => c.trim());
  const header = cells(rows[0] ?? '');
  const body = rows.slice(2); // la ligne 1 est le séparateur |---|
  let html = '<div class="md-table-wrap"><table class="md-table"><thead><tr>';
  for (const h of header) html += `<th>${inline(h)}</th>`;
  html += '</tr></thead><tbody>';
  for (const r of body) {
    html += '<tr>';
    for (const c of cells(r)) html += `<td>${inline(c)}</td>`;
    html += '</tr>';
  }
  return html + '</tbody></table></div>';
}

const LIST_ITEM = /^\s*([-*]|\d+\.)\s+(.*)$/;

export function renderMarkdown(src: string): string {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let i = 0;
  let para: string[] = [];

  const flushPara = (): void => {
    if (para.length === 0) return;
    out.push(`<p>${inline(para.join(' '))}</p>`);
    para = [];
  };

  while (i < lines.length) {
    const line = lines[i] ?? '';
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      flushPara();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? '').trim().startsWith('```')) {
        buf.push(lines[i] ?? '');
        i++;
      }
      i++; // fence de fermeture
      out.push(`<pre class="md-pre"><code>${escapeHtml(buf.join('\n'))}</code></pre>`);
      continue;
    }
    if (trimmed === '') {
      flushPara();
      i++;
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushPara();
      const level = Math.min(6, (heading[1] ?? '#').length);
      out.push(`<h${level} class="md-h md-h${level}">${inline(heading[2] ?? '')}</h${level}>`);
      i++;
      continue;
    }
    if (/^(-{3,}|\*{3,})$/.test(trimmed)) {
      flushPara();
      out.push('<hr class="md-hr">');
      i++;
      continue;
    }
    if (trimmed.startsWith('|')) {
      flushPara();
      const rows: string[] = [];
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        rows.push(lines[i] ?? '');
        i++;
      }
      out.push(renderTable(rows));
      continue;
    }
    if (trimmed.startsWith('>')) {
      flushPara();
      const buf: string[] = [];
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('>')) {
        buf.push((lines[i] ?? '').trim().replace(/^>\s?/, ''));
        i++;
      }
      out.push(`<blockquote class="md-quote">${inline(buf.join(' '))}</blockquote>`);
      continue;
    }
    const bullet = LIST_ITEM.exec(line);
    if (bullet) {
      flushPara();
      const ordered = /^\d+\.$/.test(bullet[1] ?? '');
      const items: string[] = [];
      while (i < lines.length) {
        const m = LIST_ITEM.exec(lines[i] ?? '');
        if (!m) break;
        let item = inline(m[2] ?? '');
        i++;
        // Lignes de continuation indentées (sans marqueur de liste).
        while (i < lines.length && /^\s{2,}\S/.test(lines[i] ?? '') && !LIST_ITEM.test(lines[i] ?? '')) {
          item += ` ${inline((lines[i] ?? '').trim())}`;
          i++;
        }
        items.push(`<li>${item}</li>`);
      }
      const tag = ordered ? 'ol' : 'ul';
      out.push(`<${tag} class="md-list">${items.join('')}</${tag}>`);
      continue;
    }
    para.push(trimmed);
    i++;
  }
  flushPara();
  return out.join('\n');
}
